import {Typography} from "@mui/material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {getGridNumericOperators, getGridSingleSelectOperators, getGridStringOperators} from "@mui/x-data-grid-premium";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {SUPPLIER_ENTITY_TYPE_LABEL} from "~/pages/supplier-360/constants/supplierEntityLabels";
import {SUPPLIER_ENTITY_TYPES, type SupplierEntity} from "~/pages/supplier-360/types/Supplier360Types";
import {SupplierCountryFlag} from "~/pages/supplier-360/components/SupplierCountryFlag";
import {CountryColumnHeader} from "~/pages/supplier-360/components/CountryColumnHeader";
import {SupplierEntityTypeTag} from "../entityTags/SupplierEntityTypeTag";
import {SupplierScoreTag} from "../entityTags/SupplierScoreTag";
import {SupplierCanonicalNameCell} from "./SupplierCanonicalNameCell";

const ENTITY_TYPE_OPTIONS = SUPPLIER_ENTITY_TYPES.map(value => ({
    value,
    label: SUPPLIER_ENTITY_TYPE_LABEL[value],
}));

/**
 * Country and Business unit take exact + list; entity_type is exact-only; Score and Spend are the
 * `__gte` / `__lte` range filters (§2.1).
 */
const ENTITY_TYPE_FILTER_OPERATORS = getGridSingleSelectOperators().filter(o => o.value === 'is');
const EXACT_OR_ANY_OF_OPERATORS = getGridStringOperators().filter(
    o => o.value === 'equals' || o.value === 'isAnyOf',
);
const RANGE_FILTER_OPERATORS = getGridNumericOperators().filter(o => o.value === '>=' || o.value === '<=');

export function getSupplierNormalizedColumns(): GridColDef<SupplierEntity>[] {
    return [
        {
            field: 'canonical_name',
            headerName: 'Normalized name',
            flex: 1.5,
            minWidth: 220,
            // Name search is the page-level `?search=` field, not a column filter.
            filterable: false,
            renderCell: ({row}) => (
                <SupplierCanonicalNameCell
                    entityId={row.id}
                    canonicalName={row.canonical_name}
                    logoUrl={row.logo_url}
                    memberCount={row.member_count}/>
            ),
        },
        {
            field: 'business_units',
            headerName: 'Business unit',
            flex: 1,
            minWidth: 150,
            // A list, so not orderable; filterable by name against the whole cluster's transactions (§2.1).
            sortable: false,
            filterOperators: EXACT_OR_ANY_OF_OPERATORS,
            // Every name, so export and the tooltip carry the full list, not just the first one.
            valueGetter: (_value, row) => row.business_units?.join(', ') ?? '',
            renderCell: ({row}) => {
                const businessUnits = row.business_units ?? [];
                if (businessUnits.length === 0) return '—';

                return <Typography component="span">
                    {businessUnits.join(', ')}
                </Typography>
            },
        },
        {
            field: 'confidence',
            headerName: 'Score',
            type: 'number',
            headerAlign: 'left',
            align: 'center',
            flex: 0.6,
            minWidth: 100,
            filterOperators: RANGE_FILTER_OPERATORS,
            renderCell: ({row}) => <SupplierScoreTag score={row.confidence}/>,
        },
        {
            field: 'se_country',
            headerName: 'Country',
            flex: 0.5,
            minWidth: 120,
            align: 'center',
            renderHeader: () => <CountryColumnHeader/>,
            sortable: false,
            filterOperators: EXACT_OR_ANY_OF_OPERATORS,
            valueGetter: (_value, row) => row.se_country?.code_a2 ?? '',
            renderCell: ({row}) => <SupplierCountryFlag country={row.se_country}/>,
        },
        {
            field: 'entity_type',
            headerName: 'Type',
            type: 'singleSelect',
            valueOptions: ENTITY_TYPE_OPTIONS,
            flex: 0.8,
            minWidth: 140,
            filterOperators: ENTITY_TYPE_FILTER_OPERATORS,
            renderCell: ({row}) => <SupplierEntityTypeTag entityType={row.entity_type}/>,
        },
        {
            field: 'spend',
            headerName: 'Spend',
            type: 'number',
            headerAlign: 'left',
            flex: 0.8,
            minWidth: 120,
            filterOperators: RANGE_FILTER_OPERATORS,
            valueGetter: (_value, row) => Number(row.spend ?? 0),
            renderCell: ({value}) => <CurrencyComponent v={value} nDigits={3}/>,
        },
    ];
}
